import React, { useEffect, useState } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import { getMapTileUrl } from './dopplerApi';
import { useDopplerPrefetch } from './utils/useDopplerPrefetch';

/**
 * DopplerTileLayers - Renders radar tile layers with prefetching.
 *
 * This component wraps the TileLayer rendering and adds RTK Query prefetching
 * to preload upcoming animation frames for smoother playback.
 *
 * @param {Object} props
 * @param {Date} props.baseTime - Rounded base time for tile URLs
 * @param {Date[]} props.frames - Array of frame times
 * @param {number} props.currentFrame - Current frame index (sliderValue)
 * @param {boolean} props.isPlaying - Whether animation is playing
 * @param {Function} props.onPrefetchProgress - Callback for prefetch progress updates
 */
export default function DopplerTileLayers({
  baseTime,
  frames,
  currentFrame,
  isPlaying,
  onPrefetchProgress,
}) {
  const map = useMap();
  const [mapReady, setMapReady] = useState(false);

  // Wait for map to be ready
  useEffect(() => {
    if (map) {
      const handleReady = () => setMapReady(true);

      // Check if already ready
      if (map.getZoom() !== undefined) {
        setMapReady(true);
      }

      map.on('load', handleReady);
      map.on('moveend', handleReady);

      return () => {
        map.off('load', handleReady);
        map.off('moveend', handleReady);
      };
    }
  }, [map]);

  // Use prefetch hook
  const { prefetchStatus, getPrefetchProgress } = useDopplerPrefetch({
    baseTime,
    frames,
    currentFrame,
    isPlaying,
    prefetchCount: 3,
    layer: 'future_radar',
  });

  // Report progress to parent (only when prefetchStatus changes)
  useEffect(() => {
    if (onPrefetchProgress) {
      const total = frames.length;
      const loaded = Object.values(prefetchStatus).filter((s) => s === 'loaded').length;
      const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
      onPrefetchProgress({ loaded, total, percent });
    }
  }, [prefetchStatus, frames.length]); // removed onPrefetchProgress and getPrefetchProgress

  if (!mapReady) return null;

  return (
    <>
      {frames.map((frame, index) => (
        <TileLayer
          key={`radar-${index}-${frame.toISOString()}`}
          url={getMapTileUrl(baseTime, frame)}
          attribution="Fusion by RescAlert"
          opacity={currentFrame === index ? 1 : 0}
          // Add loading class based on prefetch status
          className={prefetchStatus[index] === 'loading' ? 'tile-loading' : ''}
        />
      ))}
    </>
  );
}

/**
 * PrefetchProgressIndicator - Shows loading progress for animation frames.
 */
export function PrefetchProgressIndicator({ progress, style }) {
  if (!progress || progress.percent === 100) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 12,
        ...style,
      }}
    >
      Loading: {progress.loaded}/{progress.total} frames ({progress.percent}%)
    </div>
  );
}
